import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import Network from '@/Network'
import type { ChangeReasons } from '@/Network/Types/Requests/ChangeReasonRequests'
import type {
  VacancyBucket,
  VacancyCenterResponse,
  VacancyItem,
  VacancyRemindResponse,
  VacancyStatusFilter
} from '@/Network/Types/Responses/VacancyCenterResponses'
import { getApiErrorMessage } from '@/utils/apiError'
import { counterLabel, sectorLabel } from './vacancyCenterModel'
import type { VacancyAppliedChip } from './vacancyFilterModel'
import {
  DEFAULT_VACANCY_VIEW_STATE,
  createVacancyViewState,
  toVacancyApiQuery,
  toggleFilterValue,
  type VacancyFilterKind,
  type VacancyGroupBy,
  type VacancyViewState
} from './vacancyCenterUrl'

export const VACANCY_CENTER_LOAD_ERROR = 'Não foi possível carregar a Central de vagas'

/**
 * Último nome conhecido de cada valor de filtro, por tipo (SPEC 2.5a.1 §2.1):
 * um item marcado pode sumir de `counters`, mas o dropdown e o chip continuam
 * com o nome. Chave = o `value` da opção (`'3'`, `'sector:12'`, `'none'`…).
 */
export type VacancyFilterNames = Record<VacancyFilterKind, Record<string, string>>

export const emptyFilterNames = (): VacancyFilterNames => ({
  unit: {},
  schedule: {},
  sector: {},
  specialty: {}
})

/**
 * Central de vagas (SPEC 2.5a §4.6, 2.5a.1 §5). O store NÃO se reseta quando a
 * página desmonta: o contador do menu (`menuCount`) vale para a sessão inteira.
 */
export const useVacancyCenterStore = defineStore('vacancyCenter', () => {
  const view = ref<VacancyViewState>(createVacancyViewState())
  const data = ref<VacancyCenterResponse | null>(null)
  const isLoading = ref(false)
  const isRefreshing = ref(false)
  const loadError = ref<string | null>(null)
  const refreshFailed = ref(false)
  const lastLoadedAt = ref<number | null>(null)
  const isPanelOpen = ref(false)
  const panelStationId = ref<number | null>(null)
  const remindingIds = ref<number[]>([])
  const filterNames = ref<VacancyFilterNames>(emptyFilterNames())
  /** `VacancyCounter` não traz a unidade do setor: o subtítulo do dropdown vem dos itens. */
  const sectorUnitNames = ref<Record<number, string>>({})

  // Descarta respostas atrasadas (vários toggles seguidos na barra de filtros).
  let fetchId = 0
  // Carga normal (não silenciosa) em voo: um silencioso nunca passa por cima dela.
  let normalLoad: Promise<void> | null = null

  const menuCount = computed<number | null>(() => data.value?.totals.all ?? null)

  /** Acumula nomes (contadores + itens) e o mapa setor → unidade da resposta. */
  function learnNames(response: VacancyCenterResponse): void {
    const next: VacancyFilterNames = {
      unit: { ...filterNames.value.unit },
      schedule: { ...filterNames.value.schedule },
      sector: { ...filterNames.value.sector },
      specialty: { ...filterNames.value.specialty }
    }
    const units = { ...sectorUnitNames.value }
    response.counters.byHealthUnit.forEach((counter) => {
      if (counter.id !== null) next.unit[String(counter.id)] = counterLabel(counter, 'unit')
    })
    response.counters.bySector.forEach((counter) => {
      next.sector[counter.key] = counterLabel(counter, 'sector')
    })
    response.counters.bySpecialty.forEach((counter) => {
      next.specialty[counter.key] = counterLabel(counter, 'specialty')
    })
    response.items.forEach((item) => {
      next.unit[String(item.healthUnitId)] = item.healthUnitName
      next.schedule[String(item.scheduleId)] = item.scheduleName
      if (item.sector) {
        next.sector[`sector:${item.sector.id}`] ??= sectorLabel(item.sector)
        units[item.sector.id] = item.healthUnitName
      }
    })
    filterNames.value = next
    sectorUnitNames.value = units
  }

  function rememberName(kind: VacancyFilterKind, value: string, name: string): void {
    filterNames.value = {
      ...filterNames.value,
      [kind]: { ...filterNames.value[kind], [value]: name }
    }
  }

  /**
   * `silent` (polling / recarga depois de uma ação): sem esqueleto e, se
   * falhar, mantém a lista exibida e só liga "Falha ao atualizar".
   */
  function fetch(options: { silent?: boolean } = {}): Promise<void> {
    const silent = options.silent === true
    // Um polling/recarga silenciosa durante a troca de filtro não pode descartar
    // a carga normal (a mais nova não silenciosa vence): pega carona nela.
    if (silent && normalLoad) return normalLoad
    const load = runFetch(silent)
    if (!silent) {
      normalLoad = load
      void load.finally(() => {
        if (normalLoad === load) normalLoad = null
      })
    }
    return load
  }

  async function runFetch(silent: boolean): Promise<void> {
    const id = ++fetchId
    if (silent) isRefreshing.value = true
    else {
      isLoading.value = true
      loadError.value = null
    }
    try {
      const response = await Network.vacancyCenter.list(toVacancyApiQuery(view.value))
      if (id !== fetchId) return
      data.value = response
      learnNames(response)
      loadError.value = null
      refreshFailed.value = false
      lastLoadedAt.value = Date.now()
    } catch (error) {
      if (id !== fetchId) return
      if (silent) {
        refreshFailed.value = true
      } else {
        // Sem lista "vazia" mentirosa: a tela mostra o erro com "Tentar de novo".
        data.value = null
        loadError.value = getApiErrorMessage(error, VACANCY_CENTER_LOAD_ERROR)
      }
    } finally {
      if (id === fetchId) {
        isLoading.value = false
        isRefreshing.value = false
      }
    }
  }

  function hydrate(next: VacancyViewState): void {
    view.value = createVacancyViewState(next)
  }

  function setStatus(status: VacancyStatusFilter): Promise<void> {
    view.value = { ...view.value, status }
    return fetch()
  }

  /** Janela "Próximas 24h / 3 / 7 / 14 dias" (SPEC 2.5a.1 U5). */
  function setDays(days: number): Promise<void> {
    if (days === view.value.days) return Promise.resolve()
    view.value = { ...view.value, days }
    return fetch()
  }

  /** Cada toggle aplica na hora (padrão da Dashboard: não existe "Aplicar"). */
  function toggleFilter(kind: VacancyFilterKind, value: string, name?: string): Promise<void> {
    if (name) rememberName(kind, value, name)
    view.value = toggleFilterValue(view.value, kind, value)
    return fetch()
  }

  /** "Limpar filtros": zera status e filtros e mantém a janela (e o agrupamento/aba da tela). */
  function clearFilters(): Promise<void> {
    const { days, groupBy, tab } = view.value
    view.value = createVacancyViewState({ days, groupBy, tab })
    return fetch()
  }

  /** ✕ de um chip aplicado (barra do desktop e faixa do celular). */
  function removeChip(chip: VacancyAppliedChip): Promise<void> {
    if (chip.kind === 'status') return setStatus('all')
    if (chip.kind === 'days') return setDays(DEFAULT_VACANCY_VIEW_STATE.days)
    return toggleFilter(chip.kind, chip.value)
  }

  /** Agrupamento é só da tela: não refaz o fetch. */
  function setGroupBy(groupBy: VacancyGroupBy): void {
    view.value = { ...view.value, groupBy }
  }

  /** Aba de urgência do celular (`aba` na URL): só da tela. */
  function setTab(tab: VacancyBucket | null): void {
    view.value = { ...view.value, tab }
  }

  function replaceItem(item: VacancyItem): void {
    if (!data.value) return
    data.value = {
      ...data.value,
      items: data.value.items.map((current) =>
        current.stationId === item.stationId ? item : current
      )
    }
  }

  /** "Cobrar confirmação": o botão vira "✓ Cobrado às HH:mm" até `nextReminderAt`. */
  async function remind(item: VacancyItem): Promise<VacancyRemindResponse> {
    remindingIds.value = [...remindingIds.value, item.stationId]
    try {
      const response = await Network.vacancyCenter.remind(item.stationId)
      const current = data.value?.items.find((i) => i.stationId === item.stationId) ?? item
      replaceItem({
        ...current,
        lastReminderAt: response.sentAt,
        nextReminderAt: response.nextAllowedAt
      })
      return response
    } finally {
      remindingIds.value = remindingIds.value.filter((id) => id !== item.stationId)
    }
  }

  /**
   * "✓ Alocar": manda o médico que a tela mostrava (o backend responde 409 se
   * o plantão mudou), troca o item pelo recalculado (o card vira aguardando na
   * hora) e recarrega a lista em silêncio.
   */
  async function allocate(
    item: VacancyItem,
    doctorId: number,
    options: { preAllocate?: boolean; changeReasons?: ChangeReasons } = {}
  ): Promise<VacancyItem> {
    const updated = await Network.vacancyCenter.allocate(item.stationId, {
      doctorId,
      expectedCurrentDoctorId: item.doctor?.id ?? null,
      // Pré-alocação (D24/D45): só quando pedida; ausente = alocação comum (backend antigo ignora).
      ...(options.preAllocate ? { preAllocate: true } : {}),
      // E1: no topo do corpo; sem motivo (vaga aberta interna) o corpo é o de hoje.
      ...(options.changeReasons ? { changeReasons: options.changeReasons } : {})
    })
    replaceItem(updated)
    void fetch({ silent: true })
    return updated
  }

  /** Painel aberto pausa o polling (a lista não muda debaixo da escolha). */
  function openPanel(stationId: number): void {
    isPanelOpen.value = true
    panelStationId.value = stationId
  }

  function closePanel(): void {
    isPanelOpen.value = false
    panelStationId.value = null
  }

  function $reset(): void {
    // Invalida respostas em voo: nada chega depois do reset.
    fetchId++
    normalLoad = null
    view.value = createVacancyViewState()
    data.value = null
    isLoading.value = false
    isRefreshing.value = false
    loadError.value = null
    refreshFailed.value = false
    lastLoadedAt.value = null
    isPanelOpen.value = false
    panelStationId.value = null
    remindingIds.value = []
    filterNames.value = emptyFilterNames()
    sectorUnitNames.value = {}
  }

  return {
    view,
    data,
    isLoading,
    isRefreshing,
    loadError,
    refreshFailed,
    lastLoadedAt,
    isPanelOpen,
    panelStationId,
    remindingIds,
    filterNames,
    sectorUnitNames,
    menuCount,
    fetch,
    hydrate,
    setStatus,
    setDays,
    toggleFilter,
    clearFilters,
    removeChip,
    setGroupBy,
    setTab,
    replaceItem,
    remind,
    allocate,
    openPanel,
    closePanel,
    $reset
  }
})
